import { TIRE_POSITIONS, costPerUnit, type TirePosition, type VehicleExpenseCategory } from './fleet-efficiency';

/**
 * P12 Quản lý vỏ xe (docs/phases/P12.md §17; đề xuất "Đổ dầu và giờ cẩu" mục 6, 6b, 6c – anh Tiến duyệt 11/10/2026).
 * Hàm thuần dùng chung server (quyết định, báo cáo) + giao diện (xem trước sơ đồ, đề xuất đảo vỏ):
 * - sơ đồ vị trí theo số trục (dùng lại 11 vị trí TIRE_POSITIONS của phiếu chi phí xe);
 * - màu từng bánh (xanh / vàng / đỏ) theo số lần vá và mã quét lúc kiểm;
 * - km đã chạy, chi phí trên mỗi km của vỏ;
 * - đến hạn đảo vỏ, đề xuất thứ tự đảo theo độ sâu gai (không đảo theo cảm tính);
 * - so giá với lần trước cùng hạng mục (6c): "Cùng một hạng mục sao giá cao hơn".
 * Mọi ngưỡng là tham số lưu DB (`fleet_tire_policy`), số dưới đây là mặc định đề xuất (anh và người phụ trách kỹ thuật kiểm lại).
 */

// ---------------------------------------------------------------------------
// Tham số
// ---------------------------------------------------------------------------
export interface FleetTirePolicy {
  /** Vỏ cũ tháo ra phải nộp kho trong số ngày này, quá hạn báo quản lý */
  returnDays: number;
  /** Vá từ số lần này → đỏ, đề nghị rút vỏ */
  redPatches: number;
  /** Vá từ số lần này trong `watchKm` km đầu → vàng (theo dõi) */
  watchPatches: number;
  watchKm: number;
  /** Chu kỳ đảo vỏ (km) */
  rotationKm: number;
  /** Hai bánh ghép đôi lệch gai quá số mm này → đảo sớm / không ghép */
  pairDiffMm: number;
  /** Gai tối thiểu (mm): dưới mức này rút vỏ. Chưa đặt (null) cho tới khi lấy đúng quy chuẩn kiểm định hiện hành */
  minTreadMm: number | null;
}

export const FLEET_TIRE_POLICY_DEFAULT: FleetTirePolicy = {
  returnDays: 3,
  redPatches: 5,
  watchPatches: 3,
  watchKm: 5_000,
  rotationKm: 10_000,
  pairDiffMm: 3,
  minTreadMm: null,
};

export const FLEET_TIRE_POLICY_LABELS: Record<keyof FleetTirePolicy, string> = {
  returnDays: 'Vỏ cũ phải nộp kho trong (ngày)',
  redPatches: 'Vá từ số lần này thì đỏ, đề nghị rút vỏ',
  watchPatches: 'Vá từ số lần này trong km đầu thì vàng',
  watchKm: 'Km đầu để tính vá nhiều (km)',
  rotationKm: 'Chu kỳ đảo vỏ (km)',
  pairDiffMm: 'Bánh đôi lệch gai tối đa (mm)',
  minTreadMm: 'Gai tối thiểu (mm, theo quy chuẩn kiểm định)',
};

// ---------------------------------------------------------------------------
// Sơ đồ vị trí theo số trục
// ---------------------------------------------------------------------------
export const TIRE_AXLE_OPTIONS = [2, 3] as const;
export type TireAxles = (typeof TIRE_AXLE_OPTIONS)[number];
export const TIRE_AXLE_LABELS: Record<TireAxles, string> = { 2: '2 trục: 6 bánh + 1 dự phòng', 3: '3 trục: 10 bánh + 1 dự phòng' };

/** Một trục trên sơ đồ: trục trước bánh đơn, trục sau bánh đôi (trái ngoài, trái trong, phải trong, phải ngoài). */
export interface TireAxleRow {
  kind: 'front' | 'dual';
  label: string;
  positions: TirePosition[];
}

export function tireAxleRows(axles: TireAxles): TireAxleRow[] {
  const front: TireAxleRow = { kind: 'front', label: 'Trục trước', positions: ['FL', 'FR'] };
  const rear: TireAxleRow = { kind: 'dual', label: 'Trục sau (bánh đôi)', positions: ['RL_OUT', 'RL_IN', 'RR_IN', 'RR_OUT'] };
  if (axles === 3) return [front, { kind: 'dual', label: 'Trục giữa (bánh đôi)', positions: ['ML_OUT', 'ML_IN', 'MR_IN', 'MR_OUT'] }, rear];
  return [front, rear];
}

/** Các vị trí của xe theo thứ tự đánh số 1…n, dự phòng (SPARE) cuối cùng. */
export function tireLayout(axles: TireAxles): TirePosition[] {
  return [...tireAxleRows(axles).flatMap((r) => r.positions), 'SPARE'];
}

/** Bên tài (trái, xe tay lái thuận) hay bên phụ. Dự phòng: null. */
export function tireSide(p: TirePosition): 'driver' | 'passenger' | null {
  if (p === 'SPARE') return null;
  return p === 'FL' || p.includes('L_') ? 'driver' : 'passenger';
}

export const TIRE_POSITION_NAMES: Record<TirePosition, string> = {
  FL: 'Trước bên tài',
  FR: 'Trước bên phụ',
  ML_OUT: 'Giữa tài ngoài',
  ML_IN: 'Giữa tài trong',
  MR_IN: 'Giữa phụ trong',
  MR_OUT: 'Giữa phụ ngoài',
  RL_OUT: 'Sau tài ngoài',
  RL_IN: 'Sau tài trong',
  RR_IN: 'Sau phụ trong',
  RR_OUT: 'Sau phụ ngoài',
  SPARE: 'Dự phòng',
};

/** Số thứ tự trên sơ đồ: "1"…"10", dự phòng "DP". */
export function tirePositionNo(axles: TireAxles, p: TirePosition): string {
  if (p === 'SPARE') return 'DP';
  const i = tireLayout(axles).indexOf(p);
  return i < 0 ? '?' : String(i + 1);
}

/** "1 · Trước bên tài" như bản mẫu. */
export const tirePositionName = (axles: TireAxles, p: TirePosition) => `${tirePositionNo(axles, p)} · ${TIRE_POSITION_NAMES[p]}`;

export const isTirePosition = (s: unknown): s is TirePosition => typeof s === 'string' && (TIRE_POSITIONS as readonly string[]).includes(s);

// ---------------------------------------------------------------------------
// Vỏ: trạng thái, màu, km, chi phí
// ---------------------------------------------------------------------------
export const TIRE_STATUSES = ['in_stock', 'mounted', 'pending_return', 'returned', 'scrapped'] as const;
export type TireStatus = (typeof TIRE_STATUSES)[number];
export const TIRE_STATUS_LABELS: Record<TireStatus, string> = {
  in_stock: 'Vỏ mới trong kho',
  mounted: 'Đang lắp trên xe',
  pending_return: 'Chờ nộp kho',
  returned: 'Vỏ cũ trong kho',
  scrapped: 'Đã thanh lý',
};

export const TIRE_EVENT_KINDS = ['register', 'install', 'remove', 'patch', 'rotate', 'check', 'measure', 'return', 'scrap'] as const;
export type TireEventKind = (typeof TIRE_EVENT_KINDS)[number];
export const TIRE_EVENT_LABELS: Record<TireEventKind, string> = {
  register: 'Nhập kho',
  install: 'Lắp vào xe',
  remove: 'Tháo ra',
  patch: 'Vá',
  rotate: 'Đảo vỏ',
  check: 'Kiểm vỏ',
  measure: 'Đo gai',
  return: 'Kho nhận vỏ cũ',
  scrap: 'Thanh lý',
};

export const TIRE_PATCH_KINDS = ['tread', 'sidewall'] as const;
export type TirePatchKind = (typeof TIRE_PATCH_KINDS)[number];
export const TIRE_PATCH_KIND_LABELS: Record<TirePatchKind, string> = { tread: 'Vá thường', sidewall: 'Vá hông' };

export type TireHealth = 'ok' | 'watch' | 'bad';
export const TIRE_HEALTH_LABELS: Record<TireHealth, string> = { ok: 'Bình thường', watch: 'Theo dõi (vá nhiều so với km)', bad: 'Vượt ngưỡng / sai mã khi kiểm' };

/** Mã vạch công ty dạng MT-L-0012 (in hoa, bỏ khoảng trắng). Số seri đúc là mã gốc; tem có thể bong. */
export const normTireCode = (s: string) => s.trim().toUpperCase().replace(/\s+/g, '');

/** Km vỏ đã chạy = km các lần lắp trước + (km xe hiện tại − km lúc lắp lần này). */
export function tireKmRun(kmBefore: number, installKm: number | null, currentKm: number | null): number {
  const now = installKm !== null && currentKm !== null ? Math.max(0, currentKm - installKm) : 0;
  return Math.round((kmBefore + now) * 10) / 10;
}

export interface TireHealthInput {
  patches: number;
  /** Số lần vá trong `watchKm` km đầu của vỏ */
  earlyPatches: number;
  /** Lần kiểm gần nhất quét ra mã khác */
  mismatch: boolean;
}

/** Xanh: bình thường; vàng: vá ≥ 3 lần trong 5.000 km đầu; đỏ: vá ≥ 5 lần hoặc quét ra sai mã. */
export function tireHealth(i: TireHealthInput, p: Pick<FleetTirePolicy, 'redPatches' | 'watchPatches'>): TireHealth {
  if (i.mismatch || i.patches >= p.redPatches) return 'bad';
  if (i.earlyPatches >= p.watchPatches) return 'watch';
  return 'ok';
}

/** Chi phí trên mỗi km của vỏ = (giá mua + tiền vá) ÷ km đã chạy (làm tròn đồng). */
export const tireCostPerKm = (purchasePrice: number | null, patchCost: number, kmRun: number) => costPerUnit((purchasePrice ?? 0) + patchCost, kmRun);

// ---------------------------------------------------------------------------
// Đảo vỏ
// ---------------------------------------------------------------------------
export interface RotationDueInput {
  currentKm: number | null;
  /** Km lúc đảo lần trước (hoặc km lắp bộ vỏ nếu chưa đảo lần nào) */
  lastRotationKm: number | null;
  /** Độ lệch gai lớn nhất giữa 2 bánh ghép đôi ở lần đo gần nhất (mm) */
  maxPairDiffMm: number | null;
}
export interface RotationDue {
  dueInKm: number | null;
  due: boolean;
  message: string;
}

export function rotationDue(i: RotationDueInput, p: Pick<FleetTirePolicy, 'rotationKm' | 'pairDiffMm'>): RotationDue {
  const fmt = (v: number) => Math.round(v).toLocaleString('vi-VN');
  if (i.maxPairDiffMm !== null && i.maxPairDiffMm > p.pairDiffMm)
    return { dueInKm: 0, due: true, message: `Đảo sớm: bánh đôi lệch gai ${i.maxPairDiffMm.toLocaleString('vi-VN')} mm (quá ${p.pairDiffMm} mm)` };
  if (i.currentKm === null || i.lastRotationKm === null) return { dueInKm: null, due: false, message: 'Chưa đủ số km để tính hạn đảo vỏ' };
  const left = Math.round(i.lastRotationKm + p.rotationKm - i.currentKm);
  if (left <= 0) return { dueInKm: left, due: true, message: `Đã tới hạn đảo vỏ (quá ${fmt(-left)} km; đảo mỗi ${fmt(p.rotationKm)} km, lần trước ở km ${fmt(i.lastRotationKm)})` };
  return { dueInKm: left, due: false, message: `Đến hạn đảo vỏ sau ${fmt(left)} km nữa (đảo mỗi ${fmt(p.rotationKm)} km, lần trước ở km ${fmt(i.lastRotationKm)})` };
}

export interface RotationTire {
  tireId: number;
  code: string;
  position: TirePosition;
  treadMm: number;
  patches: number;
  /** Vỏ đắp lại: không lắp trục trước */
  retread: boolean;
  /** Vỏ có vết vá hông: không lắp trục trước */
  sidewallPatched: boolean;
  /** Vỏ có chiều quay: chỉ đảo cùng bên */
  directional: boolean;
}
export interface RotationMove {
  tireId: number;
  code: string;
  from: TirePosition;
  /** null = rút ra, nộp kho */
  to: TirePosition | null;
  treadMm: number;
  reason: string;
}
export interface RotationPlan {
  moves: RotationMove[];
  notes: string[];
  warnings: string[];
}

/**
 * Đề xuất đảo vỏ theo quy tắc 6b:
 * 1. rút vỏ gai dưới mức tối thiểu hoặc vá từ `redPatches` lần;
 * 2. xếp vỏ còn lại (cả dự phòng) theo độ sâu gai; 2 vỏ tốt nhất (không đắp lại, không vá hông) lên trục trước;
 * 3. còn lại ghép cặp bánh đôi theo thứ tự gai (cặp lệch ≤ `pairDiffMm`), vỏ gai sâu hơn trong cặp lắp phía ngoài;
 * 4. vỏ có chiều quay chỉ đảo cùng bên (đổi cặp trái ↔ phải nếu cần); vỏ thừa (gai thấp nhất) làm dự phòng.
 */
export function suggestRotation(axles: TireAxles, tires: RotationTire[], p: Pick<FleetTirePolicy, 'minTreadMm' | 'redPatches' | 'pairDiffMm'>): RotationPlan {
  const fmt = (v: number) => v.toLocaleString('vi-VN', { maximumFractionDigits: 1 });
  const rows = tireAxleRows(axles);
  const wheels = rows.flatMap((r) => r.positions);
  const notes: string[] = [];
  const warnings: string[] = [];
  const to = new Map<number, { pos: TirePosition | null; reason: string }>();
  const pool: RotationTire[] = [];
  for (const t of tires) {
    const low = p.minTreadMm !== null && t.treadMm < p.minTreadMm;
    if (low || t.patches >= p.redPatches)
      to.set(t.tireId, { pos: null, reason: `rút ra, nộp kho (gai ${fmt(t.treadMm)} mm${low ? ` dưới mức ${fmt(p.minTreadMm!)} mm` : ''}, vá ${t.patches} lần)` });
    else pool.push(t);
  }
  pool.sort((a, b) => b.treadMm - a.treadMm || a.patches - b.patches || a.tireId - b.tireId);
  // Trục trước: 2 vỏ tốt nhất đủ điều kiện
  const frontOk = pool.filter((t) => !t.retread && !t.sidewallPatched);
  const front = frontOk.slice(0, 2);
  if (front.length < 2) warnings.push('Không đủ 2 vỏ đủ điều kiện cho trục trước (vỏ đắp lại hoặc có vá hông không lắp trục trước). Lấy vỏ mới trong kho.');
  // Vỏ có chiều quay giữ bên cũ
  const sideOf = (t: RotationTire) => tireSide(t.position) ?? 'driver';
  if (front.length === 2) {
    const [a, b] = front as [RotationTire, RotationTire];
    const swap = (a.directional && sideOf(a) === 'passenger') || (b.directional && sideOf(b) === 'driver');
    const [l, r] = swap ? [b, a] : [a, b];
    to.set(l.tireId, { pos: 'FL', reason: `trục trước (gai ${fmt(l.treadMm)} mm)` });
    to.set(r.tireId, { pos: 'FR', reason: `trục trước (gai ${fmt(r.treadMm)} mm)` });
    notes.push(`Trục trước: ${l.code} và ${r.code} (gai ${fmt(l.treadMm)} / ${fmt(r.treadMm)} mm, tốt nhất trên xe)`);
  } else front.forEach((t, i) => to.set(t.tireId, { pos: i === 0 ? 'FL' : 'FR', reason: `trục trước (gai ${fmt(t.treadMm)} mm)` }));
  const rest = pool.filter((t) => !front.includes(t));
  const dualRows = rows.filter((r) => r.kind === 'dual');
  const pairs: [TirePosition, TirePosition][] = dualRows.flatMap((r) => [
    [r.positions[0]!, r.positions[1]!] as [TirePosition, TirePosition],
    [r.positions[3]!, r.positions[2]!] as [TirePosition, TirePosition],
  ]);
  const need = pairs.length * 2;
  const forDuals = rest.slice(0, need);
  const extra = rest.slice(need);
  // Ghép cặp liên tiếp theo thứ tự gai (lệch nhỏ nhất)
  const groups: RotationTire[][] = [];
  for (let i = 0; i < forDuals.length; i += 2) groups.push(forDuals.slice(i, i + 2));
  // Vỏ có chiều quay: cặp phải nằm đúng bên. Các cặp trái (chỉ số chẵn) / phải (lẻ) – đổi chỗ 2 cặp cùng trục nếu sai bên.
  for (let g = 0; g + 1 < groups.length; g += 2) {
    const left = groups[g]!;
    const right = groups[g + 1]!;
    const wrong = left.some((t) => t.directional && sideOf(t) === 'passenger') || right.some((t) => t.directional && sideOf(t) === 'driver');
    if (wrong) [groups[g], groups[g + 1]] = [right, left];
  }
  groups.forEach((grp, gi) => {
    const [outPos, inPos] = pairs[gi]!;
    const [a, b] = grp;
    if (a) to.set(a.tireId, { pos: outPos, reason: b ? `ghép đôi với ${b.code}` : 'bánh đôi' });
    if (b) to.set(b.tireId, { pos: inPos, reason: `ghép đôi với ${a!.code}` });
    if (a && b) {
      const d = Math.round(Math.abs(a.treadMm - b.treadMm) * 10) / 10;
      if (d > p.pairDiffMm) warnings.push(`Cặp ${a.code} / ${b.code} lệch gai ${fmt(d)} mm, quá ${fmt(p.pairDiffMm)} mm: nên thay vỏ mới cho cặp này`);
      else notes.push(`${a.code} ↔ ${b.code}: ghép đôi gai lệch ${fmt(d)} mm (≤ ${fmt(p.pairDiffMm)} mm)`);
    }
  });
  for (const t of [...front, ...forDuals]) {
    const side = tireSide(to.get(t.tireId)!.pos!);
    if (t.directional && side && side !== sideOf(t)) warnings.push(`${t.code} có chiều quay nhưng phải đổi bên: kiểm tra lại trước khi lắp`);
  }
  const filled = front.length + forDuals.length;
  if (filled < wheels.length) warnings.push(`Thiếu ${wheels.length - filled} vỏ cho các vị trí lăn bánh: lấy vỏ mới trong kho`);
  if (extra[0]) to.set(extra[0].tireId, { pos: 'SPARE', reason: 'làm dự phòng' });
  for (const t of extra.slice(1)) to.set(t.tireId, { pos: null, reason: 'thừa vỏ: nộp kho' });
  const moves: RotationMove[] = tires.map((t) => {
    const d = to.get(t.tireId) ?? { pos: null, reason: 'nộp kho' };
    return { tireId: t.tireId, code: t.code, from: t.position, to: d.pos, treadMm: t.treadMm, reason: d.reason };
  });
  return { moves, notes, warnings };
}

/** Độ lệch gai lớn nhất giữa các cặp bánh đôi đang lắp (mm) – null khi chưa đo đủ. */
export function maxPairDiff(axles: TireAxles, treadByPosition: Partial<Record<TirePosition, number | null>>): number | null {
  let max: number | null = null;
  for (const r of tireAxleRows(axles).filter((x) => x.kind === 'dual')) {
    for (const [a, b] of [
      [r.positions[0]!, r.positions[1]!],
      [r.positions[2]!, r.positions[3]!],
    ] as const) {
      const x = treadByPosition[a];
      const y = treadByPosition[b];
      if (x == null || y == null) continue;
      const d = Math.round(Math.abs(x - y) * 10) / 10;
      max = max === null ? d : Math.max(max, d);
    }
  }
  return max;
}

// ---------------------------------------------------------------------------
// 6c: so giá với lần trước cùng hạng mục
// ---------------------------------------------------------------------------
export const PRICE_HIGHER_MESSAGE = 'Cùng một hạng mục sao giá cao hơn';

/** Loại phiếu có hạng mục lặp lại để so giá: vá lốp, thay / nổ lốp, rửa xe, cầu đường. */
export const PRICE_CHECK_CATEGORIES = ['tire_patch', 'tire_replace', 'tire_blowout', 'wash', 'toll'] as const satisfies readonly VehicleExpenseCategory[];
export const priceCheckApplies = (c: VehicleExpenseCategory) => (PRICE_CHECK_CATEGORIES as readonly string[]).includes(c);

/** Bỏ dấu, thường hóa tên trạm thu phí / nơi làm để so "cùng trạm". */
export function normPlace(s: string | null | undefined): string {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Khóa hạng mục để so giá: vá thường so vá thường, vá hông so vá hông; thay lốp so cùng cỡ lốp; cầu đường so cùng trạm;
 * rửa xe so rửa xe. Trả `fallback` = khóa rộng hơn khi chưa xác định được (thay lốp chưa biết cỡ, cầu đường chưa ghi trạm).
 */
export function priceKey(i: { category: VehicleExpenseCategory; patchKind?: TirePatchKind | null; tireSize?: string | null; vendor?: string | null }): { key: string; prefix: boolean } | null {
  switch (i.category) {
    case 'tire_patch':
      return { key: `tire_patch:${i.patchKind ?? 'tread'}`, prefix: false };
    case 'tire_replace':
    case 'tire_blowout': {
      const size = normPlace(i.tireSize);
      return size ? { key: `tire_new:${size}`, prefix: false } : { key: 'tire_new:', prefix: true };
    }
    case 'toll': {
      const st = normPlace(i.vendor);
      return st ? { key: `toll:${st}`, prefix: false } : { key: 'toll:', prefix: true };
    }
    case 'wash':
      return { key: 'wash', prefix: false };
    default:
      return null;
  }
}

export interface PricePrev {
  expenseId: number;
  amount: number;
  occurredAt: string;
  vendor: string | null;
}
export const priceIsHigher = (prev: PricePrev | null, amount: number) => !!prev && amount > prev.amount;
